import { useTranslation } from 'react-i18next';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, theme, Tooltip } from 'antd';
import { useJobSlotStartAndEndDisplayTimes } from 'pos-client/components/scheduleBoard/hooks/useJobSlotStartAndEndDisplayTimes.ts';
import { useJobStatusTextColor } from 'pos-client/components/scheduleBoard/hooks/useJobStatusTextColor.ts';
import { PosLabel } from 'pos-client/components/shared/Form/PosLabel.tsx';
import { LIGHT_GREY } from 'pos-client/utils/colours.ts';
import type { ScheduleBoardBlockedSlotModel } from 'pos-shared/models/schedulingSchema.ts';

import { JobStatusIcon } from './JobStatusIcon.tsx';

interface BlockedSlotTooltipProps {
  slotData: ScheduleBoardBlockedSlotModel;
  children: React.ReactNode | React.ReactNode[];
}

// This component for now looks very similar to the BlockedSlotContent component, but it will change soon when designs are ready.
export const BlockedSlotTooltip = ({
  slotData,
  children,
}: BlockedSlotTooltipProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const textColor = useJobStatusTextColor(slotData.state);
  const { startDisplay, endDisplay } = useJobSlotStartAndEndDisplayTimes(
    slotData.startDateDisplay,
    slotData.endDateDisplay,
    false
  );

  const warningIcon =
    slotData.state === 'Unauthorized' ? (
      <JobStatusIcon status={'unauthorized-warning'} />
    ) : (
      ''
    );

  return (
    <Tooltip
      styles={{
        root: {
          minWidth: 500,
          border: `1px solid ${LIGHT_GREY}`,
          borderRadius: token.borderRadius,
        },
        body: { padding: token.padding },
      }}
      placement="top"
      color={token.colorWhite}
      title={
        <div
          style={{
            color: textColor,
            fontSize: token.fontSizeSM,
          }}
        >
          <Flex justify="space-between" align="center">
            <p style={{ fontWeight: token.fontWeightStrong, marginBottom: 0 }}>
              {`${slotData.state} ${t('scheduling.blockedSlot.blocked')} `}
              {warningIcon}
            </p>
            <JobStatusIcon status={slotData.state} />
          </Flex>

          <Flex
            align="center"
            style={{
              marginTop: token.marginXXS,
              marginBottom: token.marginXXS,
            }}
          >
            <FontAwesomeIcon
              icon={faClock}
              style={{
                fontSize: token.fontSizeSM,
                marginRight: token.marginXS,
              }}
            />
            <span>{`${startDisplay} - ${endDisplay}`}</span>
          </Flex>

          <PosLabel boldLabel label={t('scheduling.blockedSlot.reason')} />
          <p id="blocked-slot-description">{slotData.description}</p>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};
